import { useMemo, type FC } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  formatDollars,
  formatPhoneNumber,
  formatSSN,
  formatDate as formatShortDate,
  formatPersonDateForDisplay,
  formatRelativeTime,
} from "@/shared/utils/formatters";
import { useEstateLastActivity } from "@/shared/hooks/useEstateLastActivity";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { AlertTriangle, Eye, EyeOff, DollarSign } from "lucide-react";

import { AssetSearchShortcuts } from "@/pages/lawyer/client-detail/components/AssetSearchShortcuts";
import { KeyDocumentsChecklist } from "@/pages/lawyer/client-detail/components/KeyDocumentsChecklist";
import { Database } from "@/shared/types/database.types";
import { supabase } from "@/shared/lib/supabase";
import { DonutChart } from "@/shared/components/DonutChart";
import { getEstateDonutData } from "@/shared/utils/estateMath";
import type { EstateItem } from "@/shared/types/estateTypes";

type EstateRow = Database["public"]["Tables"]["estates"]["Row"];
type PersonDocumentRow = Database["public"]["Tables"]["estate_documents"]["Row"];

interface EstateAccountSummaryCardProps {
  estateId: number | undefined;
  onViewEstateAccount: () => void;
}

const EstateAccountSummaryCard: FC<EstateAccountSummaryCardProps> = ({
  estateId,
  onViewEstateAccount,
}) => {
  const { data: accountData } = useQuery({
    queryKey: ["lawyer-estate-account-summary", estateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("plaid_connections")
        .select("id, status, last_refreshed_at")
        .eq("estate_id", estateId as number)
        .eq("purpose", "estate_account")
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1);
      if (error) throw error;
      const row = data?.[0] ?? null;
      return row ? { connected: true, lastSyncedAt: row.last_refreshed_at } : { connected: false, lastSyncedAt: null };
    },
    enabled: !!estateId,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Card>
      <CardContent className="pt-6">
        <h3 className="text-lg font-semibold text-primary mb-4">Estate Account</h3>
        {accountData?.connected ? (
          <div className="flex flex-col gap-2">
            {accountData.lastSyncedAt && (
              <p className="text-sm text-secondary">
                Last synced {formatRelativeTime(accountData.lastSyncedAt)}
              </p>
            )}
            <Button
              variant="link"
              size="sm"
              className="text-brand self-start p-0"
              onClick={onViewEstateAccount}
            >
              View account
            </Button>
          </div>
        ) : (
          <p className="text-sm text-secondary py-4">Not connected</p>
        )}
      </CardContent>
    </Card>
  );
};

// Extended document type with computed fields that may be added at runtime
type LawyerDocument = PersonDocumentRow & {
  name?: string;
  file_size?: number;
};

interface AccountOwner {
  full_name: string;
  email?: string;
  phone_number?: string | null;
}

interface LawyerOverviewTabProps {
  person: EstateRow | null;
  owner: AccountOwner | null | undefined;
  ownerLegalRole: string[];
  documents: LawyerDocument[] | undefined;
  naDocumentTypes: Set<string> | undefined;
  estateItems: EstateItem[];
  showSSN: boolean;
  setShowSSN: (show: boolean) => void;
  isLoadingSSN: boolean;
  decryptedSSN: string | null | undefined;
  onDocumentClick: (doc: LawyerDocument) => void;
  onViewAllDocuments: () => void;
  onViewAllEstateFinances: () => void;
  onViewAssetSearch: () => void;
  onViewEstateAccount: () => void;
}

export default function LawyerOverviewTab({
  person,
  owner,
  ownerLegalRole,
  documents,
  naDocumentTypes,
  estateItems,
  showSSN,
  setShowSSN,
  isLoadingSSN,
  decryptedSSN,
  onDocumentClick,
  onViewAllDocuments,
  onViewAllEstateFinances,
  onViewAssetSearch,
  onViewEstateAccount,
}: LawyerOverviewTabProps) {
  const ownerUserId = person?.owner_user_id;
  const estateId = person?.estate_id;

  const { data: lastActivity } = useEstateLastActivity(estateId ?? undefined);

  const { data: relationshipToDecedent, isLoading: isRelationshipLoading } = useQuery({
    queryKey: ["lawyer-owner-relationship", ownerUserId, estateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_estates")
        .select("relationship_to_decedent")
        .eq("user_id", ownerUserId as number)
        .eq("estate_id", estateId as number)
        .maybeSingle();

      if (error) {
        throw error;
      }

      return (data?.relationship_to_decedent as string | null) ?? "";
    },
    enabled: !!ownerUserId && !!estateId,
    staleTime: 5 * 60 * 1000,
  });
  const {
    segments: donutSegments,
    netValue,
    totalAssets: totalAssetsAbs,
    totalDebts: totalDebtsAbs,
  } = useMemo(() => getEstateDonutData(estateItems || []), [estateItems]);

  const formattedRelationshipToDecedent = (relationshipToDecedent ?? "")
    .split("_")
    .map((part) => (part ? part[0]?.toUpperCase() + part.slice(1).toLowerCase() : ""))
    .filter((part) => part.length > 0)
    .join(" ");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Decedent Information - Left Column */}
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-primary mb-6">Estate Overview</h3>

            <div className="flex flex-col">
              <div className="grid grid-cols-[200px_1fr] items-center gap-4 px-4 py-3 bg-subtle rounded-sm">
                <span className="text-secondary">Full Name</span>
                <span className="text-primary">{person?.full_name || "—"}</span>
              </div>
              <div className="grid grid-cols-[200px_1fr] items-center gap-4 px-4 py-3 rounded-sm">
                <span className="text-secondary">Last Known Residence</span>
                <span className="text-primary">{person?.location || "—"}</span>
              </div>
              <div className="grid grid-cols-[200px_1fr] items-center gap-4 px-4 py-3 bg-subtle rounded-sm">
                <span className="text-secondary">Date of Birth</span>
                <span className="text-primary">
                  {formatPersonDateForDisplay(person?.date_of_birth, {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
              <div className="grid grid-cols-[200px_1fr] items-center gap-4 px-4 py-3 rounded-sm">
                <span className="text-secondary">SSN</span>
                {!person?.ssn_encrypted ? (
                  <span className="text-tertiary">No SSN on file</span>
                ) : (
                  <div className="flex items-center gap-2">
                    {showSSN ? (
                      isLoadingSSN ? (
                        <span className="text-secondary">Decrypting...</span>
                      ) : decryptedSSN ? (
                        <span className="text-primary font-mono">{formatSSN(decryptedSSN)}</span>
                      ) : (
                        <span className="text-red-500">Unable to decrypt</span>
                      )
                    ) : (
                      <span className="text-tertiary font-mono">XXX-XX-XXXX</span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowSSN(!showSSN)}
                      className="h-8 w-8 p-0"
                      aria-label={showSSN ? "Hide SSN" : "Show SSN"}
                    >
                      {showSSN ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-[200px_1fr] items-center gap-4 px-4 py-3 bg-subtle rounded-sm">
                <span className="text-secondary">Date of Death</span>
                <span className="text-primary">
                  {formatPersonDateForDisplay(person?.date_of_death, {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
              <div className="grid grid-cols-[200px_1fr] items-center gap-4 px-4 py-3 rounded-sm">
                <span className="text-secondary">Created</span>
                <span className="text-primary">
                  {person?.created_at
                    ? `${formatShortDate(person.created_at)} (${formatRelativeTime(person.created_at)})`
                    : "—"}
                </span>
              </div>
              <div className="grid grid-cols-[200px_1fr] items-center gap-4 px-4 py-3 bg-subtle rounded-sm">
                <span className="text-secondary">Last Activity</span>
                <span className="text-primary">
                  {lastActivity
                    ? `${formatShortDate(lastActivity)} (${formatRelativeTime(lastActivity)})`
                    : "—"}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {owner && (
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold text-primary mb-6">
                Primary Contact Information
              </h3>

              <div className="flex flex-col">
                <div className="grid grid-cols-[200px_1fr] items-center gap-4 px-4 py-3 bg-subtle rounded-sm">
                  <span className="text-secondary">Full Name</span>
                  <span className="text-primary">{owner.full_name || "—"}</span>
                </div>
                <div className="grid grid-cols-[200px_1fr] items-center gap-4 px-4 py-3 rounded-sm">
                  <span className="text-secondary">Email</span>
                  <span className="text-primary">{owner.email || "—"}</span>
                </div>
                <div className="grid grid-cols-[200px_1fr] items-center gap-4 px-4 py-3 bg-subtle rounded-sm">
                  <span className="text-secondary">Relationship to Decedent</span>
                  <span className="text-primary">
                    {isRelationshipLoading ? "Loading..." : formattedRelationshipToDecedent || "—"}
                  </span>
                </div>
                <div className="grid grid-cols-[200px_1fr] items-center gap-4 px-4 py-3 rounded-sm">
                  <span className="text-secondary">Phone</span>
                  <span className="text-primary">
                    {owner.phone_number ? formatPhoneNumber(owner.phone_number) : "—"}
                  </span>
                </div>
                <div className="grid grid-cols-[200px_1fr] items-center gap-4 px-4 py-3 bg-subtle rounded-sm">
                  <span className="text-secondary">Legal Role</span>
                  <span className="text-primary">
                    {ownerLegalRole.length > 0 ? ownerLegalRole.join(", ") : "—"}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <AssetSearchShortcuts onViewAssetSearch={onViewAssetSearch} />
      </div>

      {/* Estate Profile - Right Column */}
      <div className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-primary mb-6">Estate Inventory</h3>

            {!estateItems || estateItems.length === 0 ? (
              <p className="text-center text-secondary py-8">
                {owner?.full_name || "The account holder"} needs to enter financial information to
                calculate estimated value.
              </p>
            ) : (
              <>
                <div className="mb-6">
                  {donutSegments.length > 0 ? (
                    <div className="flex items-center justify-center">
                      <DonutChart
                        segments={donutSegments}
                        centerLabel="Net Estate Value"
                        centerValue={formatDollars({ amount: netValue })}
                        size={180}
                        strokeWidth={28}
                      />
                    </div>
                  ) : (
                    <p className="text-2xl font-semibold text-primary">
                      {formatDollars({ amount: netValue })}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-3 mb-6">
                  <div className="rounded-xl border border-primary px-4 py-3">
                    <div className="flex items-center gap-1.5 text-sm text-secondary">
                      <DollarSign className="h-4 w-4" />
                      Total assets
                    </div>
                    <p className="text-lg font-semibold text-emerald-600 mt-1">
                      {formatDollars({ amount: totalAssetsAbs })}
                    </p>
                  </div>
                  <div className="rounded-xl border border-primary px-4 py-3">
                    <div className="flex items-center gap-1.5 text-sm text-secondary">
                      <DollarSign className="h-4 w-4" />
                      Total debts
                    </div>
                    <p className="text-lg font-semibold text-error mt-1">
                      {formatDollars({ amount: totalDebtsAbs })}
                    </p>
                  </div>
                </div>

                {(() => {
                  const estateWithNotes = person as EstateRow & { notes?: string | null };
                  if (!estateWithNotes?.notes) return null;
                  return (
                    <div>
                      <p className="text-sm text-secondary mb-2">Notes</p>
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-amber-800">{estateWithNotes.notes}</p>
                      </div>
                    </div>
                  );
                })()}

                <div className="text-center mt-4">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={onViewAllEstateFinances}
                    className="text-brand text-sm"
                  >
                    View all {estateItems.length} estate items →
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <KeyDocumentsChecklist
          documents={documents ?? []}
          naDocumentTypes={naDocumentTypes}
          onDocumentClick={onDocumentClick}
          onViewAllDocuments={onViewAllDocuments}
        />

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-primary">Tax Status</h3>
              <Badge className="bg-emerald-100 text-emerald-700 border-transparent hover:bg-emerald-100 font-normal">
                Up to date
              </Badge>
            </div>

            <div className="flex flex-col">
              <div className="grid grid-cols-[160px_1fr] items-center gap-4 px-4 py-3 bg-subtle rounded-sm">
                <span className="text-secondary">Latest filing year</span>
                <span className="text-primary">2025</span>
              </div>
              <div className="grid grid-cols-[160px_1fr] items-center gap-4 px-4 py-3 rounded-sm">
                <span className="text-secondary">Filing status</span>
                <span className="text-primary">No unfiled returns found</span>
              </div>
              <div className="grid grid-cols-[160px_1fr] items-center gap-4 px-4 py-3 bg-subtle rounded-sm">
                <span className="text-secondary">Payment plan</span>
                <span className="text-primary">None</span>
              </div>
              <div className="grid grid-cols-[160px_1fr] items-center gap-4 px-4 py-3 rounded-sm">
                <span className="text-secondary">Civil penalties</span>
                <span className="text-primary">None found</span>
              </div>
            </div>

            <p className="text-sm text-secondary mt-4">
              No tax issues identified from available records.
            </p>
          </CardContent>
        </Card>

        <EstateAccountSummaryCard
          estateId={estateId ?? undefined}
          onViewEstateAccount={onViewEstateAccount}
        />
      </div>
    </div>
  );
}
